import React, { useState } from 'react';
import { Phone, CheckCircle, ArrowRight, Clock, Award, Shield, ChevronDown, MessageCircle, Loader2 } from 'lucide-react';
import { heroImages, companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GreenLabourCardPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    phoneNumber: '',
    email: '',
    niNumber: '',
    homeAddress: '',
    postcode: '',
    gdprConsent: false
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.niNumber.trim()) newErrors.niNumber = 'NI number is required';
    if (!formData.homeAddress.trim()) newErrors.homeAddress = 'Home address is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.gdprConsent) newErrors.gdprConsent = 'You must agree to be contacted';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSubmitError('');

    try {
      const response = await fetch(`${API}/apply/green-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      const data = await response.json();
      console.log('Application submitted:', data);
      
      setIsSubmitted(true);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('Failed to submit your application. Please try again or call us directly.');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-construction-darkblue text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={heroImages.main} 
            alt="CSCS Green Labour Card" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-construction-darkblue via-construction-darkblue/90 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-construction-yellow text-construction-darkblue px-4 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
              🔥 Fast Track Your Career
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Apply for Your <span className="text-construction-yellow">CSCS Green Labour Card</span> Today
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Complete your Level 1 Award course and get qualified to work on UK construction sites.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105 shadow-lg"
              >
                <Phone size={24} />
                Call Now to Apply
              </a>
              <a
                href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I want to apply for a Green Labour Card`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105 shadow-lg"
              >
                <MessageCircle size={24} />
                WhatsApp Us
              </a>
              <button
                onClick={scrollToForm}
                className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
              >
                Request a Call Back
                <ChevronDown size={24} />
              </button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm md:text-base">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-construction-yellow" size={20} />
                <span>Same Day Response</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-construction-yellow" size={20} />
                <span>Fast Processing</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-construction-yellow" size={20} />
                <span>Expert Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-8 text-center">
              Why You Need a Green CSCS Card
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-construction-yellow">
                <div className="flex items-start gap-4">
                  <div className="bg-construction-yellow/10 p-3 rounded-lg">
                    <CheckCircle className="text-construction-yellow" size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-construction-darkblue mb-2">
                      Required for Site Work
                    </h3>
                    <p className="text-gray-700">
                      Most UK construction sites require all workers to hold a valid CSCS card before entry.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-construction-yellow">
                <div className="flex items-start gap-4">
                  <div className="bg-construction-yellow/10 p-3 rounded-lg">
                    <Award className="text-construction-yellow" size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-construction-darkblue mb-2">
                      Entry-Level Labourer Card
                    </h3>
                    <p className="text-gray-700">
                      Perfect for those starting their construction career as a general labourer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-construction-yellow">
                <div className="flex items-start gap-4">
                  <div className="bg-construction-yellow/10 p-3 rounded-lg">
                    <Shield className="text-construction-yellow" size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-construction-darkblue mb-2">
                      Legal Compliance
                    </h3>
                    <p className="text-gray-700">
                      Demonstrates you have the necessary health and safety knowledge required by UK law.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-construction-yellow">
                <div className="flex items-start gap-4">
                  <div className="bg-construction-yellow/10 p-3 rounded-lg">
                    <Clock className="text-construction-yellow" size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-construction-darkblue mb-2">
                      Improves Job Opportunities
                    </h3>
                    <p className="text-gray-700">
                      Opens doors to more construction jobs and demonstrates your commitment to the trade.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg">
              <p className="text-lg font-semibold text-construction-darkblue mb-2">
                ✨ Start Working Immediately
              </p>
              <p className="text-gray-700">
                Once you receive your Green Labour Card, you can start working on UK construction sites right away!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-12 text-center">
              How It Works
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="relative bg-white p-8 rounded-xl shadow-sm text-center">
                <div className="bg-construction-yellow text-construction-darkblue w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                  Submit Your Details
                </h3>
                <p className="text-gray-700">
                  Fill in the application form below with your personal information
                </p>
              </div>

              <div className="relative bg-white p-8 rounded-xl shadow-sm text-center">
                <div className="bg-construction-yellow text-construction-darkblue w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                  We Contact You
                </h3>
                <p className="text-gray-700">
                  Our team will call you to arrange your Level 1 Award course
                </p>
              </div>

              <div className="relative bg-white p-8 rounded-xl shadow-sm text-center">
                <div className="bg-construction-yellow text-construction-darkblue w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                  Get Your Card
                </h3>
                <p className="text-gray-700">
                  Complete test and receive your Green Labour Card
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="application-form" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {isSubmitted ? (
              <div className="bg-green-50 border-2 border-green-500 rounded-xl p-8 text-center">
                <CheckCircle className="text-green-600 mx-auto mb-4" size={64} />
                <h2 className="text-3xl font-bold text-green-700 mb-4">
                  Thank You for Your Application!
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  Our team will contact you shortly to complete your Green Labour Card application.
                </p>
                <p className="text-gray-600 mb-4">
                  You should receive a call within 24 hours during business hours.
                </p>
                <div className="bg-white p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600 mb-2">Need immediate assistance?</p>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="inline-flex items-center gap-2 text-construction-yellow font-bold text-lg hover:text-yellow-600"
                  >
                    <Phone size={20} />
                    Call us now: {companyInfo.phone}
                  </a>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
                    Apply Now – Enter Your Details
                  </h2>
                  <p className="text-lg text-gray-600">
                    Fill in your details below and our team will call you to complete your Green Labour Card application.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="bg-gray-50 p-8 rounded-xl">
                  <div className="space-y-6">
                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border ${errors.fullName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow`}
                        placeholder="John Smith"
                      />
                      {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                    </div>

                    {/* Date of Birth */}
                    <div>
                      <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow`}
                      />
                      {errors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Phone Number */}
                      <div>
                        <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow`}
                          placeholder="07XXX XXXXXX"
                        />
                        {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow`}
                          placeholder="john@example.com"
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>
                    </div>

                    {/* NI Number */}
                    <div>
                      <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                        National Insurance (NI) Number *
                      </label>
                      <input
                        type="text"
                        name="niNumber"
                        value={formData.niNumber}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border ${errors.niNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow`}
                        placeholder="QQ 12 34 56 C"
                      />
                      {errors.niNumber && <p className="text-red-500 text-sm mt-1">{errors.niNumber}</p>}
                    </div>

                    {/* Home Address */}
                    <div>
                      <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                        Home Address *
                      </label>
                      <input
                        type="text"
                        name="homeAddress"
                        value={formData.homeAddress}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border ${errors.homeAddress ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow`}
                        placeholder="123 High Street, London"
                      />
                      {errors.homeAddress && <p className="text-red-500 text-sm mt-1">{errors.homeAddress}</p>}
                    </div>

                    {/* Postcode */}
                    <div>
                      <label className="block text-sm font-semibold text-construction-darkblue mb-2">
                        Postcode *
                      </label>
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border ${errors.postcode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow`}
                        placeholder="SW1A 1AA"
                      />
                      {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
                    </div>

                    {/* GDPR Consent */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="gdprConsent"
                          checked={formData.gdprConsent}
                          onChange={handleChange}
                          className="mt-1 w-5 h-5 text-construction-yellow focus:ring-construction-yellow border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          I agree to be contacted regarding my CSCS Green Labour Card application. *
                        </span>
                      </label>
                      {errors.gdprConsent && <p className="text-red-500 text-sm mt-2">{errors.gdprConsent}</p>}
                    </div>

                    {/* Submit Error */}
                    {submitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {submitError}
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      data-testid="submit-green-card-btn"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application
                          <ArrowRight size={20} />
                        </>
                      )}
                    </button>

                    <p className="text-sm text-gray-600 text-center">
                      By submitting this form, you agree to our privacy policy and terms of service.
                    </p>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Urgency Section */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-construction-darkblue">
            Don't Risk Being Turned Away From Site
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-700">
            Apply for your Green Labour Card today and start working on UK construction sites immediately
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 bg-construction-darkblue text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-black transition-all hover:scale-105 shadow-lg"
            >
              Apply Now
              <ArrowRight size={20} />
            </button>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105 shadow-lg"
            >
              <Phone size={24} />
              Call {companyInfo.phone}
            </a>
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I want to apply for a Green Labour Card`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105 shadow-lg"
            >
              <MessageCircle size={24} />
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GreenLabourCardPage;
